import type { AnyBlock, TemplateDef } from "@juro/shared";
import type { TenantProfession } from "../api/tenantProfessionsApi.js";

/**
 * 書類種別 → 発行者職能キー（PROFESSION_BY_DOC_TYPE 相当）・資格の日本語ラベル
 * （PROFESSION_FULL_LABEL 相当）は Phase 2 で `tenant_document_types` /
 * `tenant_professions` のクエリ結果ベースに置き換えられた
 * （`../hooks/useDocTypeProfession.ts` の `useDocTypeProfession()` を使う）。
 * 資格の表示ラベルは `tenantProfessionsApi.list()` の結果から
 * 該当 profession の `qualification_label` を都度引く（テナントごとに編集可能な
 * 値をそのまま使えるため、固定辞書より正確）。
 */

/**
 * テンプレートが参照する発行者情報キー（正規化済み）。
 * - "name" / "qualification"          … 資格者氏名・資格名（issuer.name / issuer.qualification）
 * - "registration_number"            … 士業登録番号（issuer.registration_number）
 * - "business_label"                 … 業務表示（issuer.business_label）
 * - "invoice_registration_number"    … 適格請求書発行事業者番号（invoice.registration_number）
 * - "rules.<field_key>"              … カスタム項目（issuer.rules.<field_key>）
 */
export type IssuerRef =
  | "name"
  | "qualification"
  | "registration_number"
  | "business_label"
  | "invoice_registration_number"
  | `rules.${string}`;

function collectBlockStrings(block: AnyBlock, out: string[], refs: Set<IssuerRef>): void {
  switch (block.type) {
    case "issuer_info":
      for (const line of block.lines) {
        if (line.visible === false) continue;
        if (line.kind === "issuer_name") {
          refs.add("qualification");
          refs.add("name");
        }
        // issuer_info の registration_number 行は適格請求書番号（invoice.registration_number）を描画する
        if (line.kind === "registration_number") refs.add("invoice_registration_number");
      }
      return;
    case "kv_block":
      for (const item of block.items) {
        out.push(String(item.value ?? ""));
        if (item.data_key) out.push(item.data_key);
      }
      return;
    case "title":
    case "text":
      out.push(block.text ?? "");
      return;
    case "computed_value":
      if (block.data_key) out.push(block.data_key);
      return;
    case "settlement":
      for (const row of block.rows) out.push(row.value_key);
      return;
    case "row_container":
      for (const column of block.columns) {
        for (const child of column.blocks) collectBlockStrings(child, out, refs);
      }
      return;
    case "free_band":
      for (const child of block.children) collectBlockStrings(child.block, out, refs);
      return;
    default:
      return;
  }
}

/** テンプレート定義を走査し、参照している発行者情報キーを収集する */
export function collectIssuerRefs(template: TemplateDef): IssuerRef[] {
  const refs = new Set<IssuerRef>();
  const strings: string[] = [];
  for (const block of template.body.blocks) collectBlockStrings(block, strings, refs);

  const joined = strings.join("\n");
  for (const match of joined.matchAll(/\bissuer\.rules\.([a-z][a-z0-9_]*)/g)) {
    refs.add(`rules.${match[1]}`);
  }
  for (const match of joined.matchAll(/\bissuer\.(registration_number|business_label|name|qualification)\b/g)) {
    refs.add(match[1] as IssuerRef);
  }
  if (/\binvoice\.registration_number\b/.test(joined)) refs.add("invoice_registration_number");
  return [...refs];
}

/** 参照キーに対応するマスタの現在値。未設定なら null */
export function resolveIssuerRef(profession: TenantProfession, ref: IssuerRef): string | null {
  if (ref === "name") return profession.holder?.name ?? null;
  if (ref === "qualification") return profession.qualification_label || null;
  if (ref === "invoice_registration_number") return profession.holder?.invoice_registration_number ?? null;
  const fieldKey = ref.startsWith("rules.") ? ref.slice("rules.".length) : ref;
  const rule = profession.rules.find((item) => item.field_key === fieldKey && item.value);
  return rule?.value ?? null;
}

/** 参照キーの表示名（警告文などに使う） */
export function issuerRefLabel(ref: IssuerRef, profession?: TenantProfession | null): string {
  switch (ref) {
    case "name": return "資格者氏名";
    case "qualification": return "資格名";
    case "registration_number": return "登録番号";
    case "business_label": return "業務表示";
    case "invoice_registration_number": return "適格請求書発行事業者番号";
    default: {
      const fieldKey = ref.slice("rules.".length);
      const rule = profession?.rules.find((item) => item.field_key === fieldKey);
      return rule?.label ?? fieldKey;
    }
  }
}
